import { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea'; // I might need to create this or use standard textarea
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { Secret } from '@/hooks/useSecrets';
import { HistoryPanel } from './HistoryPanel'; // We might need to adapt this

interface SecretDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  secret: Secret | null;
  currentValue?: string;
  projectId: string;
  environmentSlug: string;
  onSave: (key: string, value: string, description?: string) => void;
  isSaving: boolean;
}

export function SecretDetailDrawer({
  isOpen,
  onClose,
  secret,
  currentValue,
  projectId,
  environmentSlug,
  onSave,
  isSaving,
}: SecretDetailDrawerProps) {
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  // Reset state when secret changes
  useEffect(() => {
    if (secret && isOpen) {
      // Sync edit value with current value when drawer opens
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEditValue(currentValue || '');
      setEditDescription(secret.description || '');
    }
  }, [secret, isOpen, currentValue]);

  const handleCopy = () => {
    navigator.clipboard.writeText(editValue);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSave = () => {
    if (secret) {
      onSave(secret.key, editValue, editDescription || undefined);
    }
  };

  if (!secret) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] flex flex-col bg-background border-l border-border p-0 gap-0">
        <SheetHeader className="px-6 py-4 border-b border-border bg-background/50 backdrop-blur">
          <SheetTitle className="font-mono text-foreground">{secret.key}</SheetTitle>
          <SheetDescription className="text-muted-foreground">
            Manage secret value and view history.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="value" className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4">
            <TabsList className="grid w-full grid-cols-2 bg-muted border border-border">
              <TabsTrigger value="value" className="data-[state=active]:bg-accent data-[state=active]:text-foreground">Current Value</TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-accent data-[state=active]:text-foreground">History</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="value" className="flex-1 flex flex-col mt-0 p-6 min-h-0 space-y-4">
            <div className="flex-1 flex flex-col space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Value</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                  >
                    {isCopied ? (
                      <span className="flex items-center gap-1 text-success">
                        <Check className="h-3 w-3" /> Copied
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Copy className="h-3 w-3" /> Copy
                      </span>
                    )}
                  </Button>
                </div>
                <Textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="min-h-[120px] font-mono text-sm bg-card border-border text-foreground resize-none focus-visible:ring-ring"
                  placeholder="Enter secret value..."
                />
                <p className="text-xs text-muted-foreground">
                  Use this editor for multi-line values like certificates or JSON.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Description</Label>
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="min-h-[60px] text-sm bg-card border-border text-foreground resize-none focus-visible:ring-ring"
                  placeholder="Brief description of this secret (optional)"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="history" className="flex-1 mt-0 min-h-0 overflow-hidden">
             <HistoryPanel
               secretKey={secret.key}
               currentValue={currentValue}
               projectId={projectId}
               environmentSlug={environmentSlug}
               onClose={() => {}} // Not needed in embedded mode
               onCopy={(val) => navigator.clipboard.writeText(val)}
               embedded={true}
             />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
